import { hasEffectivePermission, PERMISSIONS } from '@/lib/permissions'

/** @typedef {'main' | 'settings'} NavGroupId */

/**
 * @typedef {Object} AppNavEntry
 * @property {string} id
 * @property {string} path
 * @property {NavGroupId} group
 * @property {string} [labelKey]
 * @property {string} [label]
 * @property {string} [permission]
 * @property {string[]} [keywords]
 * @property {string} icon
 */

/** Salon Desk — daily menu + settings (see docs/SALON-DESK-VISION.md) */
/** @type {AppNavEntry[]} */
export const APP_NAV_ENTRIES = [
  {
    id: 'today',
    labelKey: 'nav.today',
    path: '/',
    group: 'main',
    icon: 'layout-dashboard',
    keywords: ['home', 'dashboard', 'overview'],
  },
  {
    id: 'calendar',
    labelKey: 'nav.appointments',
    path: '/calendar',
    group: 'main',
    permission: PERMISSIONS.MENU_APPOINTMENTS,
    icon: 'calendar',
    keywords: ['schedule', 'appointments', 'bookings', 'calendar'],
  },
  {
    id: 'online-bookings',
    labelKey: 'nav.onlineBookings',
    path: '/online-bookings',
    group: 'main',
    permission: PERMISSIONS.MENU_ONLINE_BOOKINGS,
    icon: 'globe',
    keywords: ['online', 'guest', 'web', 'approval', 'pending'],
  },
  {
    id: 'checkout',
    labelKey: 'nav.checkout',
    path: '/pos',
    group: 'main',
    permission: PERMISSIONS.MENU_CHECKOUT,
    icon: 'receipt',
    keywords: ['pos', 'checkout', 'payment', 'sale', 'ticket'],
  },
  {
    id: 'customers',
    labelKey: 'nav.customers',
    path: '/customers',
    group: 'main',
    permission: PERMISSIONS.MENU_CUSTOMERS,
    icon: 'contact',
    keywords: ['customers', 'clients', 'guests', 'phone'],
  },
  {
    id: 'services',
    labelKey: 'nav.services',
    path: '/services',
    group: 'main',
    permission: PERMISSIONS.MENU_SERVICES,
    icon: 'scissors',
    keywords: ['services', 'prices', 'menu', 'treatments'],
  },
  {
    id: 'products',
    labelKey: 'nav.products',
    path: '/products',
    group: 'main',
    permission: PERMISSIONS.MENU_PRODUCTS,
    icon: 'boxes',
    keywords: ['products', 'stock', 'retail', 'purchase', 'inventory'],
  },
  {
    id: 'packages',
    labelKey: 'nav.packages',
    path: '/packages',
    group: 'main',
    permission: PERMISSIONS.MENU_PACKAGES,
    icon: 'gift',
    keywords: ['packages', 'sessions', 'membership', 'combo'],
  },
  {
    id: 'staff',
    labelKey: 'nav.staff',
    path: '/staff',
    group: 'main',
    permission: PERMISSIONS.MENU_STAFF,
    icon: 'users',
    keywords: ['staff', 'team', 'stylists'],
  },
  {
    id: 'cash',
    labelKey: 'nav.cash',
    path: '/cash',
    group: 'main',
    permission: PERMISSIONS.MENU_CASH,
    icon: 'wallet',
    keywords: ['cash', 'day open', 'day close', 'z-report', 'expenses', 'close'],
  },
  {
    id: 'sales',
    labelKey: 'nav.sales',
    path: '/sales',
    group: 'main',
    permission: PERMISSIONS.ADMIN_FULL,
    icon: 'bar-chart',
    keywords: ['sales', 'revenue', 'money', 'reports', 'profit'],
  },
  {
    id: 'reports',
    labelKey: 'nav.reports',
    path: '/reports',
    group: 'main',
    permission: PERMISSIONS.ADMIN_FULL,
    icon: 'line-chart',
    keywords: ['reports', 'insights', 'analytics', 'kpi', 'profit', 'margin'],
  },
  {
    id: 'leads',
    labelKey: 'nav.leads',
    path: '/leads',
    group: 'main',
    permission: PERMISSIONS.MENU_LEADS,
    icon: 'user-plus',
    keywords: ['leads', 'pipeline', 'prospects', 'crm'],
  },
  {
    id: 'hr',
    labelKey: 'nav.hr',
    path: '/hr',
    group: 'main',
    permission: PERMISSIONS.MENU_HR,
    icon: 'badge',
    keywords: ['hr', 'leave', 'attendance', 'roster'],
  },
  {
    id: 'approvals',
    labelKey: 'nav.approvals',
    path: '/approvals',
    group: 'main',
    permission: PERMISSIONS.MENU_APPROVALS,
    icon: 'check-square',
    keywords: ['approvals', 'inbox', 'leave', 'commission'],
  },
  {
    id: 'accounts',
    labelKey: 'nav.accounts',
    path: '/accounts',
    group: 'main',
    permission: PERMISSIONS.MENU_ACCOUNTS,
    icon: 'book',
    keywords: ['accounts', 'ledger', 'coa', 'cash book', 'day book'],
  },
  {
    id: 'send-offers',
    labelKey: 'nav.sendOffers',
    path: '/send-offers',
    group: 'main',
    permission: PERMISSIONS.MENU_SEND_OFFERS,
    icon: 'megaphone',
    keywords: ['whatsapp', 'offers', 'promo', 'deals', 'marketing', 'packages'],
  },
  {
    id: 'settings',
    labelKey: 'nav.settings',
    path: '/settings',
    group: 'main',
    permission: PERMISSIONS.MENU_SETTINGS,
    icon: 'settings',
    keywords: ['settings', 'salon', 'hours', 'users', 'logo', 'online', 'booking'],
  },
]

/** @type {Record<NavGroupId, string>} */
export const NAV_GROUP_LABEL_KEYS = {
  main: 'search.groups.main',
  settings: 'search.groups.settings',
}

/** @type {NavGroupId[]} */
export const NAV_GROUP_ORDER = ['main', 'settings']

/**
 * @param {import('@/types/api').AuthUser | null | undefined} user
 * @returns {AppNavEntry[]}
 */
export function getVisibleAppNavigation(user) {
  return APP_NAV_ENTRIES.filter((entry) => {
    if (entry.permission && !hasEffectivePermission(user, entry.permission)) {
      return false
    }
    return true
  })
}

/**
 * @param {AppNavEntry} entry
 * @param {string} pathname
 */
export function isAppNavActive(entry, pathname) {
  if (entry.path === '/') {
    return pathname === '/'
  }
  return pathname === entry.path || pathname.startsWith(`${entry.path}/`)
}

/**
 * @param {import('@/types/api').AuthUser | null | undefined} user
 * @param {string} query
 * @param {(key: string) => string} t
 * @returns {AppNavEntry[]}
 */
export function filterAppNavigation(user, query, t) {
  const normalizedQuery = query.trim().toLowerCase()

  return APP_NAV_ENTRIES.filter((entry) => {
    if (entry.permission && !hasEffectivePermission(user, entry.permission)) {
      return false
    }

    if (!normalizedQuery) {
      return true
    }

    const label = entry.labelKey ? t(entry.labelKey) : (entry.label ?? '')
    const haystack = [label, ...(entry.keywords ?? []), entry.path].join(' ').toLowerCase()

    return haystack.includes(normalizedQuery)
  })
}

/**
 * @param {AppNavEntry[]} entries
 * @returns {Record<NavGroupId, AppNavEntry[]>}
 */
export function groupAppNavigation(entries) {
  /** @type {Record<NavGroupId, AppNavEntry[]>} */
  const grouped = {
    main: [],
    settings: [],
  }

  entries.forEach((entry) => {
    grouped[entry.group].push(entry)
  })

  return grouped
}

/**
 * @param {AppNavEntry} entry
 * @param {(key: string) => string} t
 */
export function appNavLabel(entry, t) {
  return entry.labelKey ? t(entry.labelKey) : (entry.label ?? entry.path)
}

/** @typedef {{ id: string, labelKey: string, entryIds: string[] }} TopNavMenuGroup */

/**
 * Primary daily tools are top-level links; secondary tools stay in short dropdowns.
 * @type {TopNavMenuGroup[]}
 */
export const TOP_NAV_MENU_GROUPS = [
  {
    id: 'today',
    labelKey: 'nav.today',
    entryIds: ['today'],
  },
  {
    id: 'appointments',
    labelKey: 'nav.appointments',
    entryIds: ['calendar'],
  },
  {
    id: 'checkout',
    labelKey: 'nav.checkout',
    entryIds: ['checkout'],
  },
  {
    id: 'customers',
    labelKey: 'nav.customers',
    entryIds: ['customers'],
  },
  {
    id: 'online-bookings',
    labelKey: 'nav.onlineBookings',
    entryIds: ['online-bookings'],
  },
  {
    id: 'catalog',
    labelKey: 'nav.groups.catalog',
    entryIds: ['services', 'products', 'packages', 'staff'],
  },
  {
    id: 'money',
    labelKey: 'nav.groups.money',
    entryIds: ['cash', 'sales', 'reports', 'accounts'],
  },
  {
    id: 'more',
    labelKey: 'nav.groups.more',
    entryIds: ['leads', 'hr', 'approvals', 'send-offers', 'settings'],
  },
]

/**
 * @param {import('@/types/api').AuthUser | null | undefined} user
 * @returns {Array<{ id: string, labelKey: string, items: AppNavEntry[] }>}
 */
export function getTopNavMenus(user) {
  const visible = getVisibleAppNavigation(user)
  const byId = new Map(visible.map((entry) => [entry.id, entry]))

  return TOP_NAV_MENU_GROUPS.map((group) => ({
    id: group.id,
    labelKey: group.labelKey,
    items: group.entryIds.map((id) => byId.get(id)).filter(Boolean),
  })).filter((group) => group.items.length > 0)
}
